import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  InputSearch,
  TableData,
  Pagination,
  UniversalFilter,
  ResetFiltersButton,
  DateRangeFilter,
  ActionDropdown,
  ActionDropdownAction,
  Status,
  ColumnSettings,
  formatHandoverSchedule,
  SCHEDULE_LEG_COPY,
} from '@/components';
import { useListQuery, usePageClamp } from '@/hooks/useUrlListState';
import { useTableColumns } from '@/hooks/useTableColumns';
import dayjs from 'dayjs';
import {
  Column,
  Handover,
  HandoverStatus,
  BUSINESS_UNIT_OPTIONS,
  getBusinessUnitLabel,
} from '@/types/common';
import { getListHandover } from '@/services/api/handovers';
import {
  copyToClipboard,
  extractApiErrorMessage,
  formatDateToApi,
} from '@/utils';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import ReturnHandoverModal from './ReturnHandoverModal';
import {
  HANDOVER_FILTER_KEYS,
  HANDOVER_LIST_DEFAULTS,
} from './handoverListQuery';

// Title-case a raw driver status (e.g. "MITRA_RESIGN" -> "Mitra Resign")
const formatDriverStatusLabel = (value: string) =>
  value
    .toLowerCase()
    .split(/[\s_]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

// Pill colors mirror the Mitra Status pill in the Booking tab
const getDriverStatusPillClass = (value: string) => {
  const normalized = value.toUpperCase();
  if (normalized.includes('SUSPEND') || normalized.includes('RESIGN')) {
    return '!bg-[#FB3748] !text-white';
  }
  return '!bg-[#E5E7EB] !text-[#6B7280]';
};

/**
 * Which pair of query params the date range travels as, derived from the rung the
 * tab lists — because "by date" means a different COLUMN on each: the planned
 * pickup, the planned return, or the actual handover.
 *
 * A waiting row has no actual date at all (`handoverDate` is null before pickup,
 * `returnDate` before the unit is back), so filtering either waiting tab on an
 * actual date matches nothing or, worse, the wrong thing.
 */
const DATE_RANGE_KEYS = (leg?: 'handover' | 'return') => {
  if (leg === 'handover') {
    return { start: 'scheduledStartDate', end: 'scheduledEndDate' };
  }
  if (leg === 'return') {
    return {
      start: 'scheduledReturnStartDate',
      end: 'scheduledReturnEndDate',
    };
  }
  return { start: 'handoverStartDate', end: 'handoverEndDate' };
};

// Only surface "abnormal" statuses — hide ACTIVE / READY_TO_WORK
const shouldShowDriverStatus = (value?: string) => {
  if (!value?.trim()) return false;
  const normalized = value.toUpperCase().replace(/\s+/g, '_');
  return normalized !== 'ACTIVE' && normalized !== 'READY_TO_WORK';
};

interface ActiveHandoverTabProps {
  onEditHandover: (handover: Handover) => void;
  refreshTrigger: number;
  /**
   * Which possession rung to list, as the CSV the API expects. Defaults to
   * ACTIVE so the original Active Handover tab is unchanged; the Waiting Return
   * tab passes WAITING_RETURN, whose rows come back in this same shape.
   */
  statuses?: string;
  /** Distinct per tab, or the two tabs would share one set of column prefs. */
  storageKey?: string;
  /**
   * The legacy return endpoint accepts ACTIVE only, so offering this on any
   * other rung is a button that can only 409.
   */
  canReturn?: boolean;
  /**
   * Which planned date this tab is due on, if any — the ONE fact that separates
   * the three tabs this component serves. It drives the schedule column, the
   * action label, and which column the date range filters.
   *
   * There used to be a second prop (`dateField`) saying the same thing, and the
   * Waiting Return tab set only this one: its range filtered the date the unit
   * went OUT while the column beside it showed the date it is due BACK. One fact,
   * one prop, so the two cannot disagree again.
   *
   * Absent on the Active tab, which has neither date pending and filters the
   * ACTUAL handover date.
   */
  scheduleLeg?: 'handover' | 'return';
  /**
   * Offer the ops date override. Only passed on the two waiting tabs — on any
   * other rung the endpoint 409s, so the action would be a button that cannot
   * work. The host owns the modal, since the same one serves the detail popup.
   */
  onEditSchedule?: (handover: Handover) => void;
  vehicles: any[];
  clients: any[];
  pitstops: any[];
  hubLocations: any[];
  oems: any[];
  models: any[];
  onTotalChange?: (total: number) => void; // New prop to communicate total back to parent
  onDownloadParamsChange?: (params: Record<string, any>) => void;
}

const ActiveHandoverTab: React.FC<ActiveHandoverTabProps> = ({
  onEditHandover,
  refreshTrigger,
  statuses = HandoverStatus.ACTIVE,
  storageKey = 'handovers-active',
  canReturn = true,
  scheduleLeg,
  onEditSchedule,
  vehicles,
  clients,
  pitstops,
  hubLocations,
  oems,
  models,
  onTotalChange,
  onDownloadParamsChange,
}) => {
  const {
    searchTerm,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    activeFilters,
    dateRange,
    datePickerSeed,
    controlsKey,
    searchInput,
    setSearchInput,
    setSearchTerm,
    setCurrentPage,
    setFilters,
    setDateRange,
    setSort,
    setPageSize,
    hasFilters,
    resetFilters,
    clampToTotal,
  } = useListQuery(HANDOVER_LIST_DEFAULTS, HANDOVER_FILTER_KEYS);
  const [handoverData, setHandoverData] = useState<Handover[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState<Handover | null>(
    null
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: any) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'clientIDs',
        title: 'Client',
        icon: 'ri-user-line',
        multiple: true,
        dataSource: clients,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'hubIDs',
        title: 'Pitstop',
        icon: 'ri-store-2-line',
        multiple: true,
        dataSource: pitstops,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'vehicleHubIDs',
        title: 'Hub Location',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: hubLocations,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-briefcase-line',
        multiple: true,
        staticOptions: BUSINESS_UNIT_OPTIONS,
      },
    ],
    [oems, models, clients, pitstops, hubLocations]
  );

  const fetchHandovers = useCallback(
    async (
      page: number = 1,
      search?: string,
      filters?: Record<string, any>,
      size?: number,
      range?: { from: Date | null; to: Date | null }
    ) => {
      setIsLoading(true);
      try {
        const params: any = {
          page: page.toString(),
          size: (size || pageSize).toString(),
          statuses,
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        // Add filters
        if (filters?.oemIDs && filters.oemIDs.length > 0) {
          params.oemIDs = filters.oemIDs.join(',');
        }
        if (filters?.modelIDs && filters.modelIDs.length > 0) {
          params.modelIDs = filters.modelIDs.join(',');
        }
        if (filters?.clientIDs && filters.clientIDs.length > 0) {
          params.clientIDs = filters.clientIDs.join(',');
        }
        if (filters?.hubIDs && filters.hubIDs.length > 0) {
          params.hubIDs = filters.hubIDs.join(',');
        }
        if (filters?.vehicleHubIDs && filters.vehicleHubIDs.length > 0) {
          params.vehicleHubIDs = filters.vehicleHubIDs.join(',');
        }
        if (filters?.businessUnits && filters.businessUnits.length > 0) {
          params.businessUnits = filters.businessUnits.join(',');
        }

        const { start: startKey, end: endKey } = DATE_RANGE_KEYS(scheduleLeg);
        if (range?.from) params[startKey] = formatDateToApi(range.from);
        if (range?.to) params[endKey] = formatDateToApi(range.to);

        const response = await getListHandover(params);

        setHandoverData(response.data || []);
        const totalCount = response.pagination?.total || 0;
        setTotal(totalCount);
        onTotalChange?.(totalCount); // Notify parent of total change

        if (response.pagination?.page) {
          setCurrentPage(response.pagination.page);
        }
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load handovers'
        );
        showErrorToast('Error', errorMessage);
        setHandoverData([]);
        setTotal(0);
        onTotalChange?.(0); // Notify parent that total is 0
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, onTotalChange, statuses, scheduleLeg, setCurrentPage]
  );

  useEffect(() => {
    fetchHandovers(currentPage, searchTerm, activeFilters, pageSize, dateRange);
  }, [
    fetchHandovers,
    currentPage,
    searchTerm,
    activeFilters,
    refreshTrigger,
    pageSize,
    dateRange,
  ]);

  // A page restored from the URL can sit past the end once the list shrinks.
  usePageClamp(clampToTotal, total, isLoading);

  useEffect(() => {
    if (!onDownloadParamsChange) return;

    const params: Record<string, any> = {
      statuses,
    };

    if (activeFilters?.oemIDs && activeFilters.oemIDs.length > 0) {
      params.oemIDs = activeFilters.oemIDs.join(',');
    }
    if (activeFilters?.modelIDs && activeFilters.modelIDs.length > 0) {
      params.modelIDs = activeFilters.modelIDs.join(',');
    }
    if (activeFilters?.clientIDs && activeFilters.clientIDs.length > 0) {
      params.clientIDs = activeFilters.clientIDs.join(',');
    }
    if (activeFilters?.hubIDs && activeFilters.hubIDs.length > 0) {
      params.hubIDs = activeFilters.hubIDs.join(',');
    }
    if (
      activeFilters?.vehicleHubIDs &&
      activeFilters.vehicleHubIDs.length > 0
    ) {
      params.vehicleHubIDs = activeFilters.vehicleHubIDs.join(',');
    }
    if (
      activeFilters?.businessUnits &&
      activeFilters.businessUnits.length > 0
    ) {
      params.businessUnits = activeFilters.businessUnits.join(',');
    }

    const rangeKeys = DATE_RANGE_KEYS(scheduleLeg);
    if (dateRange?.from) {
      params[rangeKeys.start] = formatDateToApi(dateRange.from);
    }
    if (dateRange?.to) {
      params[rangeKeys.end] = formatDateToApi(dateRange.to);
    }

    onDownloadParamsChange(params);
  }, [
    onDownloadParamsChange,
    currentPage,
    pageSize,
    searchTerm,
    activeFilters,
    dateRange,
    statuses,
    scheduleLeg,
  ]);

  // Memoized handlers to prevent function recreation on every render
  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const handleEditClick = useCallback(
    (record: Handover) => {
      onEditHandover(record);
      setActionDropdownOpen(null);
    },
    [onEditHandover]
  );

  const handleReturnClick = useCallback((record: Handover) => {
    handleReturnHandover(record);
  }, []);

  const handleCopyIDClick = useCallback(async (record: Handover) => {
    setActionDropdownOpen(null);
    if (await copyToClipboard(record.ID)) {
      showSuccessToast('Handover ID disalin', record.ID);
      return;
    }
    // Nothing was copied, so say so — a silent no-op leaves ops pasting whatever
    // was on the clipboard before.
    showErrorToast('Gagal menyalin', 'Handover ID tidak bisa disalin');
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActionDropdownOpen(null);
      }
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeyPress);
    }

    // Always cleanup on unmount
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [actionDropdownOpen]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleReturnHandover = (handover: Handover) => {
    // REPOSSESSION tidak menahan: tujuan case penarikan justru unitnya
    // kembali, jadi return yang berhasil sekaligus menutup case-nya di sisi
    // backend. Proses lain tetap menahan — di sana unitnya sedang dipakai
    // untuk sesuatu yang belum selesai. Case yang menunggu persetujuan
    // settlement juga masih ditolak, tapi itu keputusan backend: hanya di
    // sana status case-nya diketahui.
    const isVehicleUnavailable =
      handover.vehicleProcessType &&
      handover.vehicleProcessType !== 'AVAILABLE' &&
      handover.vehicleProcessType !== 'REPOSSESSION';

    if (isVehicleUnavailable) {
      showErrorToast(
        'Vehicle Not Available',
        `This vehicle is currently in ${handover.vehicleProcessType} process. The vehicle must be in AVAILABLE status before returning the handover.`
      );
      setActionDropdownOpen(null);
      return;
    }

    setSelectedHandover(handover);
    setIsReturnModalOpen(true);
    setActionDropdownOpen(null);
  };

  const handleReturnSuccess = () => {
    // Refresh the handover list after successful return
    fetchHandovers(currentPage, searchTerm, activeFilters, pageSize);
  };

  // Helper function to create action dropdown actions for handovers
  const createHandoverActions = useCallback(
    (record: Handover): ActionDropdownAction[] => [
      {
        key: 'edit',
        label: 'Edit',
        icon: 'ri-pencil-line',
        onClick: () => handleEditClick(record),
      },
      // Only on the waiting tabs, where a date is actually pending.
      ...(onEditSchedule
        ? [
            {
              key: 'edit-schedule',
              label:
                SCHEDULE_LEG_COPY[
                  scheduleLeg === 'return' ? 'RETURN' : 'HANDOVER'
                ].action,
              icon: 'ri-calendar-event-line',
              onClick: () => {
                setActionDropdownOpen(null);
                onEditSchedule(record);
              },
            },
          ]
        : []),
      // The id is a UUID: too long to be a column anyone can read, but it is
      // what ops quote in tickets and paste into queries. So it moved here,
      // where it costs no width and arrives complete.
      {
        key: 'copy-id',
        label: 'Copy Handover ID',
        icon: 'ri-file-copy-line',
        onClick: () => handleCopyIDClick(record),
        separateAfter: canReturn,
      },
      ...(canReturn
        ? [
            {
              key: 'return',
              label: 'Return',
              icon: 'ri-arrow-go-back-line',
              onClick: () => handleReturnClick(record),
            },
          ]
        : []),
    ],
    [
      handleEditClick,
      handleCopyIDClick,
      handleReturnClick,
      canReturn,
      onEditSchedule,
      scheduleLeg,
    ]
  );

  // Client-side sorting
  const sortedHandoverData = useMemo(() => {
    if (!sortField || !sortDirection) {
      return handoverData;
    }

    return [...handoverData].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === bValue) return 0;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [handoverData, sortField, sortDirection]);

  const columns: Column[] = [
    {
      key: 'platNomor',
      title: 'Vehicle',
      width: 'w-32',
      render: (value: string, record: Handover) => (
        <div>
          <span className="text-[14px] font-medium">{value}</span>
          <br />
          <span className="text-[12px] text-gray-500">
            {record.oemName + ' - ' + record.modelName}
          </span>
        </div>
      ),
    },

    {
      key: 'driverName',
      title: 'Mitra Name',
      width: 'w-32',
      render: (value: string, record: Handover) => (
        <div
          className={`text-[14px] ${
            record.driverID
              ? 'text-underline text-blue-600 cursor-pointer hover:text-blue-800'
              : 'text-gray-900 cursor-default'
          }`}
          onClick={(e) => {
            if (record.driverID) {
              e.stopPropagation();
              const backOfficeUrl =
                process.env.REACT_APP_BACK_OFFICE_URL ||
                'https://back-office.dashelectric.co';
              window.open(
                `${backOfficeUrl}/driver/${record.driverID}`,
                '_blank'
              );
            }
          }}
        >
          {value ? (
            <>
              <span className="font-medium">{value}</span>
              <br />
              <span className="text-[12px] text-gray-500">
                {record.driverCode}
              </span>
              {shouldShowDriverStatus(record.driverStatus) && (
                <div className="mt-1">
                  <Status
                    value={formatDriverStatusLabel(record.driverStatus!)}
                    type="feedback"
                    size="small"
                    className={getDriverStatusPillClass(record.driverStatus!)}
                  />
                </div>
              )}
            </>
          ) : (
            <div>-</div>
          )}
        </div>
      ),
    },
    {
      key: 'vehicleHubName',
      title: 'Hub Location',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'vehicleBusinessUnit',
      title: 'Business Unit',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? (
            getBusinessUnitLabel(value)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'clientName',
      title: 'Client',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px] font-medium">{value}</span>
      ),
    },
    {
      key: 'hubName',
      title: 'Pitstop',
      width: 'w-40',
      render: (value: string) => (
        <span className="text-[14px] font-medium">{value}</span>
      ),
    },
    // The row's own due date, on the two rungs where nothing has happened yet:
    // a unit waiting to be collected has no handoverDate and one waiting to come
    // back no returnDate, so this is the only date those tabs can show — and the
    // one ops plan their day around.
    ...(scheduleLeg
      ? [
          {
            key:
              scheduleLeg === 'return'
                ? 'scheduledReturnDate'
                : 'scheduledHandoverDate',
            title:
              scheduleLeg === 'return'
                ? 'Scheduled Return'
                : 'Scheduled Handover',
            align: 'center',
            width: 'w-40',
            render: (value: string, record: Handover) => (
              <span className="text-[14px]">
                {/* The slot window is the PICKUP's; a scheduled return is a date
                    only, so it is not passed on that leg. */}
                {formatHandoverSchedule(
                  value,
                  scheduleLeg === 'handover'
                    ? {
                        startTime: record.slotStartTime,
                        endTime: record.slotEndTime,
                      }
                    : undefined
                )}
              </span>
            ),
          } as Column,
        ]
      : []),
    // Dropped on Waiting Handover: the unit has not changed hands, so this column
    // could only ever read N/A there — the date that tab is about is the planned
    // one above.
    ...(scheduleLeg === 'handover'
      ? []
      : [
          {
            key: 'handoverDate',
            title: 'Handover Date',
            align: 'center',
            width: 'w-32',
            render: (value: string) => (
              <span className="text-[14px]">
                {value ? dayjs(value).format('DD/MM/YYYY') : 'N/A'}
              </span>
            ),
          } as Column,
        ]),
    // Dropped on Waiting Handover with Handover Date, and for the same reason:
    // duration counts from a pickup that has not happened, so it reads 0 Days on
    // every row of that tab.
    ...(scheduleLeg === 'handover'
      ? []
      : [
          {
            key: 'duration',
            title: 'Duration',
            align: 'center',
            width: 'w-24',
            render: (value: string) => {
              const numValue = parseInt(value) || 0;
              const dayText = numValue <= 1 ? 'Day' : 'Days';
              return (
                <span className="text-[14px]">{numValue + ' ' + dayText}</span>
              );
            },
          } as Column,
        ]),
    {
      key: 'sellingPrice',
      title: 'Harga Jual',
      align: 'center',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px]">
          {value
            ? new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
              }).format(Number(value)) + '/day'
            : 'Rp.0/day'}
        </span>
      ),
    },
    {
      key: 'depositMitraToDash',
      title: 'Deposit Mitra to Dash',
      align: 'center',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px]">
          {value
            ? new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
              }).format(Number(value)) + ''
            : 'Rp.0'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      sticky: 'right',
      render: (value: any, record: Handover, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={createHandoverActions(record)}
            ariaLabel={`Actions for handover ${record.platNomor}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Handover>({
    storageKey,
    columns,
  });

  return (
    <>
      {/* Controls */}
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              key={controlsKey}
              {...datePickerSeed}
              onDateRangeChange={(range) => {
                setDateRange(range);
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              debounceMs={300}
              onClear={() => handleSearch('')}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={(filters: Record<string, any>) => {
                setActionDropdownOpen(null);
                setFilters(filters);
              }}
              onClear={() => {
                setActionDropdownOpen(null);
                setFilters({});
              }}
            />
            <ResetFiltersButton
              visible={hasFilters()}
              onReset={() => resetFilters()}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <TableData
          data={sortedHandoverData}
          columns={visibleColumns}
          loading={isLoading}
          sorting={{
            field: sortField,
            direction: sortDirection,
            onSort: setSort,
          }}
          size="small"
          className="min-w-full"
          onRowClick={(record) => onEditHandover(record as Handover)}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          fetchHandovers(1, searchTerm, activeFilters, newPageSize);
        }}
      />

      {/* Return Handover Modal */}
      {canReturn && (
        <ReturnHandoverModal
          isOpen={isReturnModalOpen}
          handover={selectedHandover}
          allowedReturnReasons={[
            'CONTRACT_END',
            'MITRA_RESIGN',
            'CLIENT_REQUEST',
            'DUTY_COMPLETED',
            'REPOSSESSION',
            'OTHER',
          ]}
          onClose={() => {
            setIsReturnModalOpen(false);
            setSelectedHandover(null);
          }}
          onSuccess={handleReturnSuccess}
        />
      )}
    </>
  );
};

export default ActiveHandoverTab;
